import React from 'react';
import Modal from '../../../components/molecules/Modal/Modal';
import { PAYMENT_METHOD } from '../../../@types/Checkout.type';
import { formatPrice } from '../../../shared/utils/general.util';
import CustomIcon from '../../../components/atoms/CustomIcon';
import Text from '../../../components/atoms/Text';
import { CopySimple } from '@phosphor-icons/react';
import { ALERT_TYPE } from '../../../components/molecules/Alert';
import { showAlert } from '../../../redux/slices/alert.slice';
import { useDispatch } from 'react-redux';
import HeruLogoIcon from '../../../assets/img/svg/heru-logo-v3.svg';
import {
	PAYMENT_INSTRUCTIONS_CASH,
	PAYMENT_INSTRUCTIONS_SPEI,
} from '../../../shared/constants/payments';
import { getFormatDate } from '../../../shared/utils/dates.util';

interface Props {
	showModal: boolean;
	setShowModal: (value: boolean) => void;
	paymentMethod: string;
	total: number;
	reference: string;
	expiresAt?: string;
	referenceLink?: string;
}

const ModalReference: React.FC<Props> = ({
	paymentMethod,
	total,
	reference,
	showModal,
	setShowModal,
	expiresAt,
	referenceLink,
}) => {
	const dispatch = useDispatch();

	const copyToClipboard = () => {
		navigator.clipboard.writeText(reference).then(() => {
			const alert = {
				type: ALERT_TYPE.SUCCESS,
				title: 'Éxito',
				description: 'Texto copiado al portapapeles',
			};
			dispatch(showAlert(alert));
		});
	};

	return (
		<Modal
			sx='w-full md:max-w-xl'
			open={showModal}
			onClose={() => setShowModal(false)}
		>
			<div className='rounded-md flex flex-col shadow-md p-4'>
				<div className='mt-2 mb-4 flex items-center justify-center'>
					<img
						src={HeruLogoIcon}
						alt='heru_logo'
						className='w-14'
					/>
				</div>

				<div className='flex flex-col'>
					<div className='flex items-center justify-between'>
						<div>
							{paymentMethod === PAYMENT_METHOD.SPEI ? (
								<CustomIcon
									sx='ml-0'
									color={'var(--h-primary)'}
									name={'spei'}
									width={72}
								/>
							) : (
								<CustomIcon
									sx='ml-0'
									color={'var(--h-primary)'}
									name={'oxxo'}
									width={72}
								/>
							)}
						</div>

						<div className='flex flex-col items-end mb-4'>
							<Text size='caption'>MONTO A PAGAR</Text>
							<Text weight='bold'>$ {formatPrice(total)} MXN</Text>
						</div>
					</div>
					<Text
						color='gray'
						size='body-3'
						sx='mb-4'
					>
						{paymentMethod === PAYMENT_METHOD.SPEI
							? 'Utiliza exactamente esta cantidad al realizar el pago.'
							: 'OXXO cobrará una comisión adicional al momento de realizar el pago.'}
					</Text>
					<Text
						color='primary'
						weight='medium'
						sx='mb-2'
					>
						{paymentMethod === PAYMENT_METHOD.SPEI ? 'CLABE' : 'REFERENCIA'}
					</Text>

					<div
						onClick={copyToClipboard}
						className='flex items-center justify-center bg-h-primary rounded-md py-2 cursor-pointer'
					>
						<CopySimple
							size={18}
							color='var(--h-text-white)'
							weight='fill'
							className='mr-2'
						/>
						<Text
							color='white'
							size='xl'
							weight='bold'
							sx='tracking-wider'
						>
							{reference}
						</Text>
					</div>

					{!!expiresAt && (
						<Text
							weight='medium'
							sx='mt-3 !block text-center'
						>
							{paymentMethod === PAYMENT_METHOD.SPEI
								? 'Paga antes del '
								: 'Vence el '}
							{getFormatDate(expiresAt, 'DD MMMM YYYY', true)}
						</Text>
					)}

					{!!referenceLink && (
						<a
							href={referenceLink}
							target='_blank'
							rel='noreferrer'
							className='mt-2 text-center text-h-primary font-medium underline'
						>
							Ver mi ficha de pago
						</a>
					)}

					<div className='flex flex-col mt-4'>
						<Text
							weight='medium'
							sx='mb-4'
							color='primary'
						>
							INSTRUCCIONES
						</Text>
						<ol className='ml-4 mb-2'>
							{paymentMethod === PAYMENT_METHOD.SPEI
								? PAYMENT_INSTRUCTIONS_SPEI.map((item) => item)
								: PAYMENT_INSTRUCTIONS_CASH.map((item) => item)}
						</ol>
						<div className='flex flex-col items-center justify-center bg-h-success-95 shadow-md px-4 py-2'>
							<Text
								color='green'
								sx='text-center'
								weight='medium'
							>
								Al completar estos pasos recibirás un correo de heru confirmando
								tu pago.
							</Text>
						</div>
						<Text
							weight='light'
							size='mini-1'
							sx='!block my-4'
						>
							*La confirmación de pago puede tardar hasta 24 horas. En caso de
							no recibir el correo de confirmación, puedes contactarnos a{' '}
							<a
								href='mailto:ayuda@heru-app.com'
								className='text-h-primary'
							>
								ayuda@heru-app.com
							</a>
						</Text>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ModalReference;
